import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useWorkspace } from "../hooks/useWorkspace";
import { Users, User, Package, Copy, LogOut, CircleCheck, Lock, Mail, Inbox, Key } from "lucide-react";

export default function CuentaCompartida() {
  const { user, lang } = useAuth();
  const {
    workspaces, activeWorkspaceId, activeWorkspace, isPersonalActive,
    createInvite, joinWithCode, leaveWorkspace, unshareWorkspace, removeMember, transferDataToWorkspace, getMemberNames,
  } = useWorkspace();

  const isOwner = activeWorkspace && user && activeWorkspace.ownerId === user.uid;

  const [memberNames, setMemberNames] = useState([]);
  const [invite, setInvite] = useState(null); // { code, expiresAt }
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState("");

  const [joinCode, setJoinCode] = useState("");
  const [joinLoading, setJoinLoading] = useState(false);
  const [joinError, setJoinError] = useState("");
  const [joinSuccess, setJoinSuccess] = useState("");

  const [leaveStep, setLeaveStep] = useState(0); // 0 normal, 1 confirmando, 2 saliendo
  const [unshareStep, setUnshareStep] = useState(0); // 0 normal, 1 confirmando, 2 procesando
  const [unshareError, setUnshareError] = useState("");
  const [removingUid, setRemovingUid] = useState(null); // uid en confirmación o en proceso de expulsión
  const [removeError, setRemoveError] = useState("");

  // Traspaso de datos del personal a la cuenta compartida
  // transferStep: 0 normal, 1 elegir modo, 2 procesando, 3 hecho
  const [transferStep, setTransferStep] = useState(0);
  const [transferError, setTransferError] = useState("");
  const [transferResult, setTransferResult] = useState(null);

  useEffect(() => {
    if (!activeWorkspaceId) return;
    getMemberNames(activeWorkspaceId).then(setMemberNames);
  }, [activeWorkspaceId, workspaces]);

  // inviteModeStep: 0 nada, 1 eligiendo modo (solo si isPersonalActive)
  const [inviteModeStep, setInviteModeStep] = useState(0);

  function startCreateInvite() {
    if (isPersonalActive) {
      setInviteModeStep(1); // pedir vacío/copiar/mover antes de generar
    } else {
      handleCreateInvite("empty"); // ya estás en un compartido, no hay nada que elegir
    }
  }

  async function handleCreateInvite(mode) {
    setInviteModeStep(0);
    setInviteError(""); setInviteLoading(true);
    try {
      const result = await createInvite(activeWorkspaceId, mode);
      setInvite(result);
    } catch (err) {
      setInviteError(err.message || (lang==="en" ? "Error generating invite" : "Error al generar la invitación"));
    } finally {
      setInviteLoading(false);
    }
  }

  function copyCode() {
    if (!invite) return;
    navigator.clipboard?.writeText(invite.code);
  }

  async function handleJoin(e) {
    e.preventDefault();
    setJoinError(""); setJoinSuccess("");
    if (!joinCode.trim()) {
      setJoinError(lang==="en" ? "Enter the invitation code" : "Introduce el código de invitación");
      return;
    }
    setJoinLoading(true);
    try {
      const result = await joinWithCode(joinCode.trim());
      setJoinSuccess(lang==="en"
        ? `You've joined "${result.workspaceName}". Switch to it from the workspace selector in the sidebar.`
        : `Te has unido a "${result.workspaceName}". Cámbiate a ella desde el selector de cuentas en el menú lateral.`);
      setJoinCode("");
    } catch (err) {
      setJoinError(err.message || (lang==="en" ? "Invalid or expired code" : "Código no válido o caducado"));
    } finally {
      setJoinLoading(false);
    }
  }

  async function handleLeave() {
    setLeaveStep(2);
    try {
      await leaveWorkspace(activeWorkspaceId);
    } catch (err) {
      alert(err.message || (lang==="en" ? "Error leaving the account" : "Error al salir de la cuenta"));
      setLeaveStep(0);
    }
  }

  // Solo el propietario: deja de compartir, expulsando a todos los demás
  // miembros de golpe. El workspace vuelve a ser únicamente suyo.
  async function handleUnshare(keepData) {
    setUnshareError(""); setUnshareStep(2);
    try {
      await unshareWorkspace(activeWorkspaceId, keepData);
      setUnshareStep(0);
    } catch (err) {
      setUnshareError(err.message || (lang==="en" ? "Error stopping sharing" : "Error al dejar de compartir"));
      setUnshareStep(1);
    }
  }

  // Solo el propietario: expulsa a un miembro concreto sin afectar al resto.
  // removingUid pasa por tres estados: null (nadie seleccionado), el uid
  // (pidiendo confirmación), o "removing:<uid>" (procesando la expulsión).
  async function handleRemoveMember(memberId) {
    setRemoveError("");
    setRemovingUid(`removing:${memberId}`);
    try {
      await removeMember(activeWorkspaceId, memberId);
      setRemovingUid(null);
    } catch (err) {
      setRemoveError(err.message || (lang==="en" ? "Error removing member" : "Error al quitar al miembro"));
      setRemovingUid(null);
    }
  }

  // Traspasa entries/pendientes/recurrentes desde la cuenta personal a esta
  // cuenta compartida. mode es "copy" (se quedan también en personal) o
  // "move" (se eliminan de personal tras copiarse aquí).
  async function handleTransfer(mode) {
    setTransferError(""); setTransferStep(2);
    try {
      const result = await transferDataToWorkspace(activeWorkspaceId, mode);
      setTransferResult(result);
      setTransferStep(3);
    } catch (err) {
      setTransferError(err.message || (lang==="en" ? "Error transferring data" : "Error al traspasar los datos"));
      setTransferStep(1);
    }
  }

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h2 className="page-title" style={{display:"flex",alignItems:"center",gap:8}}><Users size={22}/> {lang==="en"?"Shared account":"Cuenta compartida"}</h2>
          <p className="page-sub">
            {lang==="en"
              ? "Share your data with another person, or join an account they've shared with you"
              : "Comparte tus datos con otra persona, o únete a una cuenta que te hayan compartido"}
          </p>
        </div>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(min(100%,380px),1fr))",gap:20,alignItems:"start"}}>

        {/* Estado actual: quién forma parte del workspace activo */}
        <div className="card">
          <h3 style={{fontSize:15,fontWeight:600,marginBottom:12,display:"flex",alignItems:"center",gap:6}}>
            {isPersonalActive
              ? <><User size={16}/> {lang==="en" ? "You're in your personal account" : "Estás en tu cuenta personal"}</>
              : <><Users size={16}/> {activeWorkspace?.name || (lang==="en" ? "Shared account" : "Cuenta compartida")}</>}
          </h3>

          {isPersonalActive ? (
            <p style={{fontSize:13,color:"var(--gray-500)"}}>
              {lang==="en"
                ? "This is your private data. Generate an invite code below to share it with someone, or switch to a shared account from the sidebar."
                : "Estos son tus datos privados. Genera un código de invitación más abajo para compartirlos con alguien, o cámbiate a una cuenta compartida desde el menú lateral."}
            </p>
          ) : (
            <>
              <p style={{fontSize:13,color:"var(--gray-500)",marginBottom:10}}>
                {lang==="en" ? "Members of this account:" : "Miembros de esta cuenta:"}
              </p>
              {removeError && <div className="alert alert-error" style={{marginBottom:10}}>{removeError}</div>}
              <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:16}}>
                {memberNames.map((member) => {
                  const isMe = user && member.uid === user.uid;
                  const isConfirming = removingUid === member.uid;
                  const isRemoving = removingUid === `removing:${member.uid}`;
                  return (
                    <div key={member.uid} style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,fontSize:13,color:"var(--gray-700)"}}>
                      <span style={{display:"flex",alignItems:"center",gap:8}}>
                        <span style={{width:8,height:8,borderRadius:"50%",background:"var(--green)",flexShrink:0}}/>
                        {member.name}{isMe ? ` (${lang==="en"?"you":"tú"})` : ""}
                      </span>

                      {isOwner && !isMe && !isConfirming && !isRemoving && (
                        <button className="btn btn-outline btn-sm" style={{padding:"2px 10px",fontSize:12}}
                                onClick={()=>setRemovingUid(member.uid)}>
                          {lang==="en"?"Remove":"Quitar"}
                        </button>
                      )}
                      {isOwner && !isMe && isConfirming && (
                        <span style={{display:"flex",gap:6}}>
                          <button className="btn btn-danger btn-sm" style={{padding:"2px 10px",fontSize:12}}
                                  onClick={()=>handleRemoveMember(member.uid)}>
                            {lang==="en"?"Confirm":"Confirmar"}
                          </button>
                          <button className="btn btn-outline btn-sm" style={{padding:"2px 10px",fontSize:12}}
                                  onClick={()=>setRemovingUid(null)}>
                            {lang==="en"?"Cancel":"Cancelar"}
                          </button>
                        </span>
                      )}
                      {isRemoving && (
                        <span style={{fontSize:12,color:"var(--gray-400)"}}>
                          {lang==="en"?"Removing...":"Quitando..."}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>

              {!isOwner && (
                <>
                  {leaveStep === 0 && (
                    <button className="btn btn-danger btn-sm" onClick={()=>setLeaveStep(1)}>
                      <LogOut size={14} style={{display:"inline",verticalAlign:"-2px",marginRight:5}}/> {lang==="en" ? "Leave this account" : "Salir de esta cuenta"}
                    </button>
                  )}
                  {leaveStep === 1 && (
                    <div style={{background:"var(--red-l)",border:"1px solid var(--red)",borderRadius:8,padding:"12px 14px"}}>
                      <p style={{fontSize:13,color:"var(--red)",marginBottom:10,fontWeight:600}}>
                        {lang==="en"
                          ? "You'll lose access to this account's data. Continue?"
                          : "Perderás el acceso a los datos de esta cuenta. ¿Continuar?"}
                      </p>
                      <div style={{display:"flex",gap:8}}>
                        <button className="btn btn-danger btn-sm" onClick={handleLeave}>
                          {lang==="en"?"Yes, leave":"Sí, salir"}
                        </button>
                        <button className="btn btn-outline btn-sm" onClick={()=>setLeaveStep(0)}>
                          {lang==="en"?"Cancel":"Cancelar"}
                        </button>
                      </div>
                    </div>
                  )}
                  {leaveStep === 2 && (
                    <p style={{fontSize:13,color:"var(--gray-500)"}}>
                      {lang==="en" ? "Leaving..." : "Saliendo..."}
                    </p>
                  )}
                </>
              )}

              {isOwner && (
                <div style={{marginBottom:16,paddingBottom:16,borderBottom:"1px solid var(--border)"}}>
                  <p style={{fontSize:12,color:"var(--gray-400)",marginBottom:10}}>
                    {lang==="en"
                      ? "This shared space is ready to use as is, or you can optionally bring in data from your personal account:"
                      : "Este espacio compartido ya está listo para usarse así, o si quieres, puedes traer datos de tu cuenta personal:"}
                  </p>
                  {transferError && <div className="alert alert-error" style={{marginBottom:10}}>{transferError}</div>}
                  {transferStep === 0 && (
                    <button className="btn btn-outline btn-sm" onClick={()=>setTransferStep(1)}>
                      <Package size={14} style={{display:"inline",verticalAlign:"-2px",marginRight:5}}/> {lang==="en" ? "Bring in my existing entries" : "Traer mis gastos ya existentes"}
                    </button>
                  )}
                  {transferStep === 1 && (
                    <div style={{background:"var(--blue-l)",border:"1px solid var(--blue)",borderRadius:8,padding:"12px 14px"}}>
                      <p style={{fontSize:13,color:"var(--gray-700)",marginBottom:10}}>
                        {lang==="en"
                          ? "This brings your personal entries, pending payments, and recurring expenses into this shared account."
                          : "Esto trae tus entradas, pendientes y gastos recurrentes de tu cuenta personal a esta cuenta compartida."}
                      </p>
                      <p style={{fontSize:13,fontWeight:600,color:"var(--gray-700)",marginBottom:10}}>
                        {lang==="en" ? "Copy (keep in personal too) or Move (remove from personal)?" : "¿Copiar (se quedan también en personal) o Mover (se eliminan de personal)?"}
                      </p>
                      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                        <button className="btn btn-primary btn-sm" onClick={()=>handleTransfer("copy")}>
                          {lang==="en"?"Copy":"Copiar"}
                        </button>
                        <button className="btn btn-danger btn-sm" onClick={()=>handleTransfer("move")}>
                          {lang==="en"?"Move":"Mover"}
                        </button>
                        <button className="btn btn-outline btn-sm" onClick={()=>setTransferStep(0)}>
                          {lang==="en"?"Cancel":"Cancelar"}
                        </button>
                      </div>
                    </div>
                  )}
                  {transferStep === 2 && (
                    <p style={{fontSize:13,color:"var(--gray-500)"}}>
                      {lang==="en" ? "Transferring..." : "Traspasando..."}
                    </p>
                  )}
                  {transferStep === 3 && transferResult && (
                    <div className="alert alert-success">
                      <CircleCheck size={13} style={{display:"inline",verticalAlign:"-2px",marginRight:4}}/> {lang==="en" ? "Done. " : "Hecho. "}
                      {transferResult.counts.entries || 0} {lang==="en"?"entries":"movimientos"}, {transferResult.counts.pendientes || 0} {lang==="en"?"pending items":"pendientes"}, {transferResult.counts.recurrents || 0} {lang==="en"?"recurring items":"recurrentes"}.
                    </div>
                  )}
                </div>
              )}

              {isOwner && (
                <>
                  {unshareError && <div className="alert alert-error" style={{marginBottom:10}}>{unshareError}</div>}
                  {unshareStep === 0 && (
                    <button className="btn btn-danger btn-sm" onClick={()=>setUnshareStep(1)}>
                      <Lock size={14} style={{display:"inline",verticalAlign:"-2px",marginRight:5}}/> {lang==="en" ? "Stop sharing this account" : "Dejar de compartir esta cuenta"}
                    </button>
                  )}
                  {unshareStep === 1 && (
                    <div style={{background:"var(--red-l)",border:"1px solid var(--red)",borderRadius:8,padding:"12px 14px"}}>
                      <p style={{fontSize:13,color:"var(--red)",marginBottom:10,fontWeight:600}}>
                        {lang==="en"
                          ? "All other members will lose access immediately, and this shared space will be deleted. What about its data?"
                          : "Todos los demás miembros perderán el acceso de inmediato, y este espacio compartido se eliminará. ¿Qué hacemos con sus datos?"}
                      </p>
                      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                        <button className="btn btn-primary btn-sm" onClick={()=>handleUnshare(true)}>
                          {lang==="en"?"Keep data (move to personal)":"Conservar datos (a mi personal)"}
                        </button>
                        <button className="btn btn-danger btn-sm" onClick={()=>handleUnshare(false)}>
                          {lang==="en"?"Discard data":"Descartar datos"}
                        </button>
                        <button className="btn btn-outline btn-sm" onClick={()=>setUnshareStep(0)}>
                          {lang==="en"?"Cancel":"Cancelar"}
                        </button>
                      </div>
                    </div>
                  )}
                  {unshareStep === 2 && (
                    <p style={{fontSize:13,color:"var(--gray-500)"}}>
                      {lang==="en" ? "Removing access..." : "Quitando acceso..."}
                    </p>
                  )}
                </>
              )}
            </>
          )}
        </div>

        {/* Invitar a alguien a la cuenta activa */}
        <div className="card">
          <h3 style={{fontSize:15,fontWeight:600,marginBottom:8}}>
            <Mail size={15} style={{display:"inline",verticalAlign:"-3px",marginRight:6}}/> {lang==="en" ? "Invite someone" : "Invitar a alguien"}
          </h3>
          <p style={{fontSize:13,color:"var(--gray-500)",marginBottom:14}}>
            {lang==="en"
              ? "Generate a code and share it (WhatsApp, email...). It's valid for 7 days and can only be used once."
              : "Genera un código y compártelo (WhatsApp, email...). Es válido durante 7 días y solo se puede usar una vez."}
          </p>

          {inviteError && <div className="alert alert-error" style={{marginBottom:10}}>{inviteError}</div>}

          {inviteModeStep === 1 && (
            <div style={{background:"var(--blue-l)",border:"1px solid var(--blue)",borderRadius:8,padding:"12px 14px",marginBottom:14}}>
              <p style={{fontSize:13,fontWeight:600,color:"var(--gray-700)",marginBottom:10}}>
                {lang==="en" ? "What should the new shared space start with?" : "¿Con qué empieza el nuevo espacio compartido?"}
              </p>
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                <button className="btn btn-primary btn-sm" onClick={()=>handleCreateInvite("empty")}>
                  <Inbox size={14} style={{display:"inline",verticalAlign:"-2px",marginRight:5}}/> {lang==="en" ? "Empty space" : "Espacio vacío"}
                </button>
                <button className="btn btn-outline btn-sm" onClick={()=>handleCreateInvite("copy")}>
                  <Copy size={14} style={{display:"inline",verticalAlign:"-2px",marginRight:5}}/> {lang==="en" ? "Copy my current entries" : "Copiar mis entradas actuales"}
                </button>
                <button className="btn btn-outline btn-sm" onClick={()=>handleCreateInvite("move")}>
                  <Package size={14} style={{display:"inline",verticalAlign:"-2px",marginRight:5}}/> {lang==="en" ? "Move my current entries" : "Mover mis entradas actuales"}
                </button>
                <button className="btn btn-outline btn-sm" onClick={()=>setInviteModeStep(0)}>
                  {lang==="en"?"Cancel":"Cancelar"}
                </button>
              </div>
            </div>
          )}

          {!invite && inviteModeStep === 0 && (
            <button className="btn btn-primary" onClick={startCreateInvite} disabled={inviteLoading}>
              {inviteLoading ? "..." : (lang==="en" ? "Generate invite code" : "Generar código de invitación")}
            </button>
          )}
          {invite && (
            <div>
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
                <div style={{fontSize:24,fontWeight:800,letterSpacing:2,color:"var(--blue)",
                             background:"var(--blue-l)",borderRadius:8,padding:"10px 16px"}}>
                  {invite.code}
                </div>
                <button className="btn btn-outline btn-sm" onClick={copyCode}>
                  <Copy size={13} style={{display:"inline",verticalAlign:"-2px",marginRight:4}}/> {lang==="en"?"Copy":"Copiar"}
                </button>
              </div>
              <p style={{fontSize:12,color:"var(--gray-400)",marginBottom:12}}>
                {lang==="en" ? "Expires: " : "Caduca: "}
                {new Date(invite.expiresAt).toLocaleDateString(lang==="en"?"en-GB":"es-ES")}
              </p>
              <button className="btn btn-outline btn-sm" onClick={()=>handleCreateInvite("empty")} disabled={inviteLoading}>
                {lang==="en" ? "Generate new code" : "Generar otro código"}
              </button>
            </div>
          )}
        </div>

        {/* Unirse a una cuenta compartida con un código */}
        <div className="card">
          <h3 style={{fontSize:15,fontWeight:600,marginBottom:8}}>
            <Key size={16} style={{display:"inline",verticalAlign:"-3px",marginRight:6}}/> {lang==="en" ? "Join a shared account" : "Unirse a una cuenta compartida"}
          </h3>
          <p style={{fontSize:13,color:"var(--gray-500)",marginBottom:14}}>
            {lang==="en"
              ? "Enter the code someone shared with you. Your personal account stays untouched."
              : "Introduce el código que te hayan compartido. Tu cuenta personal no se ve afectada."}
          </p>
          {joinError && <div className="alert alert-error" style={{marginBottom:10}}>{joinError}</div>}
          {joinSuccess && <div className="alert alert-success" style={{marginBottom:10}}>{joinSuccess}</div>}
          <form onSubmit={handleJoin} style={{display:"flex",gap:8}}>
            <input className="form-input" value={joinCode}
                   onChange={e=>setJoinCode(e.target.value.toUpperCase())}
                   placeholder={lang==="en" ? "e.g. AB12CD" : "Ej: AB12CD"}
                   style={{textTransform:"uppercase"}} maxLength={6}/>
            <button className="btn btn-primary" type="submit" disabled={joinLoading}>
              {joinLoading ? "..." : (lang==="en" ? "Join" : "Unirme")}
            </button>
          </form>
        </div>

      </div>
    </div>
  );
}
